/**
 * resources - Définitions centralisées (clé + fetcher) des ressources mises en cache.
 *
 * Avantage : la page ET le prefetch au survol utilisent EXACTEMENT la même clé et le même
 * fetcher → le préchargement remplit le cache que la page lira ensuite (ouverture instantanée).
 */

import { swrFetch, prefetch as _prefetch } from './dataCache';
import { prospectsAPI, usersAPI, tasksAPI, calendarAPI } from './api';

// ---------- Fiche dossier (ProspectDetail) ----------
export const prospectDetailKey = (id) => `prospect:${id}`;
export const prospectDetailFetcher = (id) => async () => {
    // Le dossier est obligatoire ; les pièces jointes sont non bloquantes
    // (si l'endpoint échoue, on affiche quand même la fiche).
    const [p, a] = await Promise.all([
        prospectsAPI.getById(id),
        prospectsAPI.getAttachments(id).catch(() => ({ data: { attachments: [] } })),
    ]);
    return { prospect: p.data, attachments: a.data?.attachments || [] };
};
export function prefetchProspect(id) {
    if (!id) return;
    _prefetch(prospectDetailKey(id), prospectDetailFetcher(id));
}
export function loadProspectDetail(id, opts) {
    return swrFetch(prospectDetailKey(id), prospectDetailFetcher(id), opts);
}

// ---------- Fiche utilisateur / profil équipe (UserProfile) ----------
export const userProfileKey = (id) => `userprofile:${id}`;
export const userProfileFetcher = (userId) => async () => {
    const profileRes = await usersAPI.getProfile(userId);
    const [pRes, tRes, eRes, nRes] = await Promise.allSettled([
        prospectsAPI.getAll({ assigned_to: userId, page_size: 1000 }),
        tasksAPI.getAll({ assigned_to: userId }),
        calendarAPI.getEvents({ assigned_to: userId }),
        usersAPI.getRecentNotes(userId, { limit: 30 }),
    ]);
    const raw = pRes.status === 'fulfilled' ? (pRes.value.data || []) : [];
    const prospects = raw.filter((p) =>
        p.assigned_to === userId ||
        p.dossier_apporteur_id === userId ||
        p.dossier_apporteur_2_id === userId ||
        p.dossier_vp_id === userId ||
        p.dossier_vp_2_id === userId
    );
    return {
        profile: profileRes.data,
        prospects,
        tasks: tRes.status === 'fulfilled' ? (tRes.value.data || []) : [],
        events: eRes.status === 'fulfilled' ? (eRes.value.data || []) : [],
        notes: nRes.status === 'fulfilled' ? (nRes.value.data || []) : [],
    };
};
export function prefetchUserProfile(id) {
    if (!id) return;
    _prefetch(userProfileKey(id), userProfileFetcher(id));
}
export function loadUserProfile(id, opts) {
    return swrFetch(userProfileKey(id), userProfileFetcher(id), opts);
}
